/**
 * The folder a dropped chat attachment came from, relative to what the user
 * dropped: `Brand A/Summer 2025/Instagram/Final`. The agent reads folder names
 * as meaning, so the path is kept, but it is user input that lands in the
 * sandbox and the asset library, so only a plain relative path is accepted.
 *
 * A leaf module with no runtime dependencies so packages/app can import it.
 */

export const MAX_CHAT_FILE_FOLDER_PATH_LENGTH = 1024;

export class InvalidChatFileFolderPathError extends Error {}

// C0 controls, DEL, and C1 controls.
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * Why a folder path is refused, or null when it is valid. An empty string is
 * valid and means the file was dropped on its own.
 */
export function chatFileFolderPathError(folderPath: string): string | null {
  if (folderPath.length > MAX_CHAT_FILE_FOLDER_PATH_LENGTH) {
    return `is longer than ${MAX_CHAT_FILE_FOLDER_PATH_LENGTH} characters`;
  }
  if (CONTROL_CHARACTERS.test(folderPath)) {
    return "contains a control character";
  }
  if (folderPath === "") return null;
  if (folderPath.startsWith("/")) {
    return "must be relative, without a leading /";
  }
  for (const segment of folderPath.split("/")) {
    if (segment === "") return "has an empty folder name";
    if (segment === "." || segment === "..") {
      return "must not contain . or .. folders";
    }
  }
  return null;
}

/**
 * The folder path to store for an attachment: undefined when none was sent or
 * it is empty. Throws on a path chatFileFolderPathError refuses.
 */
export function parseChatFileFolderPath(
  folderPath: string | null | undefined,
): string | undefined {
  if (folderPath == null || folderPath === "") return undefined;
  const error = chatFileFolderPathError(folderPath);
  if (error) {
    throw new InvalidChatFileFolderPathError(`folderPath ${error}`);
  }
  return folderPath;
}
